import { useReducer } from 'react'
import * as R from 'ramda'
import effects from 'app/effects/effects'
import apiConfig from 'app/configs/apiConfigs'
import { fileToBase64 } from 'libs/file'
import { getRemoteImageSize } from 'libs/imageSize'

const initialState = {
  image: undefined,
  isLoading: false,
  isStateError: false,
  errorMessage: undefined,
  pendingUploads: 0,
}

const ActionType = {
  START_LOADING: 'START_LOADING',
  INCREMENT_PENDING: 'INCREMENT_PENDING',
  DECREMENT_PENDING: 'DECREMENT_PENDING',
  SET_ERROR_IMAGE_OVER_SIZE: 'SET_ERROR_IMAGE_OVER_SIZE',
  UPLOAD_INMAGE_SUCCESS: 'UPLOAD_INMAGE_SUCCESS',
  UPLOAD_INMAGE_ERROR: 'UPLOAD_INMAGE_ERROR',
  ON_CLOSE_ERROR_DIALOG: 'ON_CLOSE_ERROR_DIALOG',
}

const TypeFile = {
  IMAGE_TYPE: 'image',
}

const MAX_RATIO = 1.91 / 1
const MIN_RATIO = 4 / 5
export const MAX_FILE_SIZE = 25
const imageMaxSize = MAX_FILE_SIZE * 1024 * 1024

// Image count thresholds
const SOFT_WARNING_THRESHOLD = 50
const SECOND_WARNING_THRESHOLD = 100
const HARD_CAP_THRESHOLD = 150

// Total payload limit
const MAX_TOTAL_PAYLOAD_MB = 250
const MAX_TOTAL_PAYLOAD_BYTES = MAX_TOTAL_PAYLOAD_MB * 1024 * 1024

const BUCKET_URL = apiConfig.awsBucketUrl

const reducer = (state, action) => {
  switch (action.type) {
    case ActionType.START_LOADING:
      return {
        ...state,
        isLoading: true,
      }
    case ActionType.INCREMENT_PENDING:
      return {
        ...state,
        isLoading: true,
        pendingUploads: state.pendingUploads + 1,
      }
    case ActionType.DECREMENT_PENDING:
      const newPending = Math.max(0, state.pendingUploads - 1)
      return {
        ...state,
        isLoading: newPending > 0,
        pendingUploads: newPending,
      }
    case ActionType.UPLOAD_INMAGE_SUCCESS:
      const remainingAfterSuccess = Math.max(0, state.pendingUploads - 1)
      return {
        ...state,
        image: action.image,
        isLoading: remainingAfterSuccess > 0,
        pendingUploads: remainingAfterSuccess,
      }
    case ActionType.UPLOAD_INMAGE_ERROR:
      const remainingAfterError = Math.max(0, state.pendingUploads - 1)
      return {
        ...state,
        isLoading: remainingAfterError > 0,
        pendingUploads: remainingAfterError,
        isStateError: true,
        errorMessage: action.errorMessage,
      }
    case ActionType.SET_ERROR_IMAGE_OVER_SIZE:
      return {
        ...state,
        isLoading: false,
        pendingUploads: 0,
        isStateError: true,
        errorMessage: action.errorMessage,
      }
    case ActionType.ON_CLOSE_ERROR_DIALOG:
      return {
        ...state,
        isStateError: false,
        errorMessage: undefined,
      }
    default: {
      return state
    }
  }
}

export const useSocialMediaSetting = (formatMessage) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const uploadImage = async (
    file,
    currentImageCount = 0,
    onWarning = null,
    currentGalleryImages = [],
    handleProgress = null,
    uploadIdMap = {},
  ) => {
    if (!R.isNil(file) && file.length > 0) {
      const filesArray = Array.from(file)
      const newImageCount = currentImageCount + filesArray.length

      // Image count validation - hard cap at 150
      // Note: This check is also done in attachments.js before uploadImage is called
      // This is a fallback check in case the validation is bypassed
      if (newImageCount > HARD_CAP_THRESHOLD) {
        const textError = formatMessage({
          id: 'compose.dialog.attachments.error.limitExceeded',
        })
        return dispatch({
          type: ActionType.SET_ERROR_IMAGE_OVER_SIZE,
          errorMessage: textError,
        })
      }

      const newFilesTotalSize = filesArray.reduce(
        (total, f) => total + (f.size || 0),
        0,
      )

      const existingGalleryTotalSize = currentGalleryImages.reduce(
        (total, img) => total + (img.size || 0),
        0,
      )

      const totalPayloadSize = existingGalleryTotalSize + newFilesTotalSize

      if (totalPayloadSize > MAX_TOTAL_PAYLOAD_BYTES) {
        const textError = formatMessage(
          {
            id: 'compose.dialog.attachments.error.totalPayloadExceeded',
          },
          { maxSize: MAX_TOTAL_PAYLOAD_MB },
        )
        return dispatch({
          type: ActionType.SET_ERROR_IMAGE_OVER_SIZE,
          errorMessage: textError,
        })
      }

      // Note: Warnings (50 and 100 thresholds) are handled in attachments.js before uploadImage is called
      // This prevents duplicate warnings and allows blocking upload until user confirms

      const allowedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/webp',
      ]

      // Validate all files before starting upload
      for (const fileItem of filesArray) {
        if (!allowedTypes.includes(fileItem.type)) {
          const textError = formatMessage({ id: 'error.400.fileTypeInvalid' })
          return dispatch({
            type: ActionType.SET_ERROR_IMAGE_OVER_SIZE,
            errorMessage: textError,
          })
        }

        if (fileItem.size >= imageMaxSize) {
          const textError = formatMessage(
            { id: 'froala.text.error.filesize' },
            { maxSize: MAX_FILE_SIZE },
          )
          return dispatch({
            type: ActionType.SET_ERROR_IMAGE_OVER_SIZE,
            errorMessage: textError,
          })
        }
      }

      // Initialize pending uploads count for all files
      for (let i = 0; i < filesArray.length; i++) {
        dispatch({
          type: ActionType.INCREMENT_PENDING,
        })
      }

      // Process all files sequentially
      for (const fileItem of filesArray) {
        try {
          const base64 = await fileToBase64(fileItem)

          const uploadId =
            uploadIdMap[fileItem.name] ||
            currentGalleryImages.find(
              (img) =>
                img.name === fileItem.name && img.uploadId && img.isLoading,
            )?.uploadId ||
            `${Date.now()}-${Math.random()}-${fileItem.name}`

          const progressHandler =
            typeof handleProgress === 'function'
              ? handleProgress(uploadId) || handleProgress
              : handleProgress || (() => {})

          const response = await effects.apis.uploadImageFile(
            { file: base64 },
            typeof progressHandler === 'function' ? progressHandler : () => {},
            uploadId,
          )

          const image = await getImageUrl(response.url, fileItem.size)

          dispatch({
            type: ActionType.UPLOAD_INMAGE_SUCCESS,
            image: {
              ...image,
              name: fileItem.name,
              uploadId,
            },
          })
        } catch (e) {
          const textError = formatMessage({ id: 'error.general' })
          dispatch({
            type: ActionType.UPLOAD_INMAGE_ERROR,
            errorMessage: textError,
          })
        }
      }
    }
  }

  const getImageId = (image) => {
    return image?.id || image?.mediaId || image?.bodyImageId
  }

  const selectImage = (item, list) => {
    const currentSelected = R.find(R.prop('isSelected'), list)
    const itemId = getImageId(item)
    const currentSelectedId = getImageId(currentSelected)
    const isCurrentlySelected =
      itemId && currentSelectedId && itemId === currentSelectedId

    return R.map((image) => {
      const imageId = getImageId(image)
      const isTargetImage = itemId && imageId && itemId === imageId
      return {
        ...image,
        isSelected:
          isCurrentlySelected && isTargetImage ? false : isTargetImage,
      }
    }, list)
  }

  const deleteImage = (item, list) => {
    const itemId = getImageId(item)
    let newImageList = R.filter((image) => {
      const imageId = getImageId(image)
      return itemId !== imageId
    }, list)

    const checkIsSelected = R.find(R.prop('isSelected'), newImageList)

    if (checkIsSelected === undefined && newImageList.length > 0) {
      newImageList[0].isSelected = true
    }

    return newImageList
  }

  const onCloseErrorDialog = () => {
    dispatch({ type: ActionType.ON_CLOSE_ERROR_DIALOG })
  }

  return {
    state,
    uploadImage,
    selectImage,
    deleteImage,
    onCloseErrorDialog,
  }
}

export const checkErrorRatio = async (image) => {
  try {
    if (!image.url) {
      return false
    }

    const imageInfo = await getRemoteImageSize(`${BUCKET_URL}/${image.url}`)
    const imageRatio = imageInfo.width / imageInfo.height

    const isErrorRatio = R.or(
      R.lt(imageRatio, MIN_RATIO),
      R.gt(imageRatio, MAX_RATIO),
    )

    return isErrorRatio
  } catch (error) {
    return true
  }
}
export const getImageUrl = (url, fileSize = null) => {
  const getFileGif = url.match('.gif')
  if (R.isNil(getFileGif)) {
    const image = {
      id: (Math.random() * 100).toString(36).replace('.', ''),
      url: `${url}`,
      isSelected: false,
      type: TypeFile.IMAGE_TYPE,
    }
    if (fileSize !== null) {
      image.size = fileSize
    }
    return image
  }
}
